/**
 * Releases the click lock a closed dialog can leave behind.
 *
 * The dialog components make the page unclickable while a modal is open by setting
 * `pointer-events: none` on the body, and take it off again when the modal closes. When
 * two modals overlap — the departure opens the invoice, a confirmation opens over a
 * form — the bookkeeping can end up with the lock still on and no modal on screen. From
 * a desk that looks like the application froze: nothing responds to a click and only a
 * reload brings it back.
 *
 * So this watches the body, and when it finds it locked with no dialog open, it takes
 * the lock off. Deliberately narrow:
 *
 *   - it only ever REMOVES a lock, and only the one on the body;
 *   - it waits before acting, because the lock is set a moment before the dialog's own
 *     markup appears and clearing it in that instant would make the modal not modal;
 *   - it checks for an open dialog first, so a modal that is genuinely open stays
 *     modal.
 *
 * It is a guard and not a cure. When it fires it says so through Sentry, once per
 * session, so the sequence that leaves the lock behind can be found and fixed at the
 * source rather than swept up here for ever.
 */
import * as Sentry from '@sentry/vue'

/** How long to let the components settle before deciding the lock is orphaned. */
const SETTLE_MS = 400

/** What counts as "a dialog is open". Popovers and menus do not lock the body. */
const OPEN_DIALOG = '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'

export const usePointerGuard = (): void => {
    if (typeof window === 'undefined') return

    let timer: ReturnType<typeof setTimeout> | null = null
    let reported = false

    const release = () => {
        timer = null

        if (document.body.style.pointerEvents !== 'none') return
        if (document.querySelector(OPEN_DIALOG)) return

        document.body.style.removeProperty('pointer-events')

        if (reported) return

        reported = true

        Sentry.captureMessage('a closed dialog left the page unclickable', 'warning')
    }

    const schedule = () => {
        if (document.body.style.pointerEvents !== 'none') return
        if (timer) clearTimeout(timer)

        timer = setTimeout(release, SETTLE_MS)
    }

    new MutationObserver(schedule).observe(document.body, {
        attributes:      true,
        attributeFilter: ['style'],
        childList:       true,
    })
}
